import { CaseSchema } from "@api/generated/types.gen";

import { DOCTOR_ACCESS_COPY, doctorAccessState } from "@constants/doctor-access";

import { formatNullableDateToLocal } from "@utils/dateUtils";

import { Column } from "../../DataTables/types/data-table";
import { STATUS_MAPPING } from "./status";

// Status mapping function to convert backend status to user-friendly labels
const getStatusLabel = (status: string): string => {
  return status in STATUS_MAPPING
    ? STATUS_MAPPING[status as keyof typeof STATUS_MAPPING]
    : status;
};

export const CaseListTableColumns: Column<CaseSchema>[] = [
  {
    id: "claim_number",
    header: "Claim Number",
    accessor: ({ claim_number }) => claim_number || "-",
    sortable: true,
    className: "py-4 font-medium",
  },
  {
    id: "template",
    header: "Template",
    accessor: ({ template }) => template?.name || "-",
    sortable: true,
    className: "py-4",
  },
  {
    id: "appointment_date",
    header: "Appointment Date",
    accessor: ({ appointment_date }) =>
      formatNullableDateToLocal(appointment_date, undefined, "-"),
    sortable: true,
    className: "py-4",
  },
  {
    id: "report_due_date",
    header: "Report Due Date",
    accessor: ({ report_due_date }) =>
      formatNullableDateToLocal(report_due_date, undefined, "-"),
    sortable: true,
    className: "py-4",
  },
  {
    id: "created_at",
    header: "Uploaded Date",
    accessor: ({ created_at }) =>
      formatNullableDateToLocal(created_at, undefined, "-"),
    sortable: true,
    className: "py-4",
  },
  {
    id: "examinee_name",
    header: "Examinee Name",
    accessor: ({ examinee_name }) => examinee_name || "-",
    sortable: true,
    className: "py-4",
  },
  {
    id: "status",
    header: "Status",
    accessor: ({ status }) => getStatusLabel(status || "") || "-",
    sortable: true,
    className: "py-4",
  },
];

/**
 * Admin-only Doctor column (MDRS-230). Replaces the earlier Yes/No "Ready for
 * doctor" text: the doctor's name is the useful primary value, with the release
 * state as a sub-line, so one column answers both "whose case is this" and "can
 * they see it". Appended by `CasesList` only for admins.
 *
 * Not sortable: the list isn't server-sorted on this field, and a client-side
 * sort over the current page only would be misleading.
 */
export const DoctorColumn: Column<CaseSchema> = {
  id: "doctor",
  header: "Doctor",
  accessor: ({ doctor_name, is_doctor_ready }) => {
    // No doctor: muted "Not assigned" with no sub-line — there's no release
    // state to report when there's nobody to release to.
    if (!doctor_name) {
      return (
        <span className="text-foreground-muted">
          {DOCTOR_ACCESS_COPY.doctorUnassigned}
        </span>
      );
    }

    return (
      <div className="min-w-0">
        <div className="truncate font-medium">{doctor_name}</div>
        <div className="mt-0.5 text-xs text-foreground-muted">
          {doctorAccessState(true, is_doctor_ready)}
        </div>
      </div>
    );
  },
  className: "py-4",
};
